import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { transparentize } from 'color2k';
import { Colors, Sizes } from 'styles';
import CloseOutlined from '@mui/icons-material/CloseOutlined';

// Selector for every focusable element inside the dialog. Used both to move focus
// to the first item on mount and to drive the Tab / Shift-Tab focus trap.
const FOCUSABLE_ELEMENTS_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
`;

const ModalContainer = styled.div`
  background-color: #0a1b28;
  border-radius: 8px;
  padding: 24px;
  max-width: 432px;
  box-shadow: 6px 6px 13px 0px rgba(0, 0, 0, 0.33);
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const HeaderBodySection = styled.div`
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
`;

const ModalTitle = styled.h2`
  flex: 1;
  font-size: ${Sizes.textDisplay2};
  font-weight: 500;
  line-height: normal;
  margin: 0 0 8px 0;
  padding-right: 2rem;
  color: ${Colors.gray10};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #ecececa3;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    color: ${Colors.white};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const BodyText = styled.p`
  font-size: ${Sizes.textBody1};
  font-weight: 400;
  color: ${Colors.gray10};
  margin: 8px 0;
`;

const WarningText = styled.p`
  font-size: ${Sizes.textBody1};
  font-weight: 600;
  color: ${Colors.white};
  margin: 0;
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: ${Sizes.textBody1};
  font-weight: 500;
  color: ${Colors.gray10};
  cursor: pointer;
  flex: 1;
  white-space: nowrap;

  input[type='checkbox'] {
    width: 14px;
    height: 14px;
    cursor: pointer;
    accent-color: ${Colors.blue2};
  }
`;

const OutlineButton = styled.button`
  background-color: ${transparentize(Colors.gray10, 0.96)};
  border: 1px solid ${Colors.gray5};
  border-radius: 8px;
  padding: 4px 16px;
  font-size: ${Sizes.textBody1};
  font-weight: 600;
  color: ${Colors.gray10};
  min-height: 32px;
  cursor: pointer;

  &:hover {
    background-color: ${transparentize(Colors.gray10, 0.85)};
  }
`;

/**
 * The app's primary scheme -- a solid `blue2` fill with near-white text, as `common/ui/Button` renders `primary` --
 * rather than the destructive red it used to carry: these dialogs confirm an intent the reader just expressed, so the
 * action is the expected answer rather than a warning. Filled rather than tinted because `blue2` is dark enough that
 * it cannot carry text on this surface.
 */
const ActionButton = styled.button`
  background-color: ${Colors.blue2};
  border: 1px solid ${Colors.blue2};
  border-radius: 8px;
  padding: 4px 16px;
  font-size: ${Sizes.textBody1};
  font-weight: 600;
  color: ${Colors.gray10};
  min-height: 32px;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${transparentize(Colors.blue2, 0.25)};
    border-color: transparent;
  }
`;

/**
 * Fixed for every one of these dialogs. The question is in the title; the buttons only have to say which way the
 * reader is answering it, and saying it the same way each time is one less thing to read under a confirmation.
 */
const PRIMARY_BUTTON_LABEL = 'Confirm';
const SECONDARY_BUTTON_LABEL = 'Cancel';

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export interface HPConfirmationModalProps {
  /** Modal title */
  title: string;
  /** Main body text */
  bodyText: string;
  /** Warning text (displayed in bold) */
  warningText?: string;
  /** Whether to show the "Don't show again" checkbox */
  showDontShowAgain?: boolean;
  /** Called when the primary action is clicked */
  onPrimaryAction: () => void;
  /** Called when the secondary (cancel) action is clicked */
  onSecondaryAction: () => void;
  /**
   * Test id for the primary action. Defaults to the shared one; the delete flow overrides it so the e2e page objects
   * that have always driven deletion by its own id keep working.
   */
  primaryActionTestId?: string;
  /** Called when the modal is closed (X button or clicking outside) */
  onClose: () => void;
  /** Called when "Don't show again" checkbox changes */
  onDontShowAgainChange?: (checked: boolean) => void;
  /** Current state of "Don't show again" checkbox */
  dontShowAgain?: boolean;
}

export const HPConfirmationModal = ({
  title,
  bodyText,
  warningText,
  showDontShowAgain = false,
  onPrimaryAction,
  onSecondaryAction,
  primaryActionTestId = 'hp-confirmation-primary',
  onClose,
  onDontShowAgainChange,
  dontShowAgain = false,
}: HPConfirmationModalProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const primaryActionRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  // On mount: save the previously-focused element and focus the primary action. First-focusable order would land on
  // the close X, which is the one control that answers nothing -- the dialog asks a question, and the action is its
  // answer, so that is where Enter should go. On unmount: restore focus so the user lands back on the trigger.
  useEffect(() => {
    previouslyFocusedRef.current = document.activeElement as HTMLElement;
    const firstFocusable = containerRef.current?.querySelector<HTMLElement>(
      FOCUSABLE_ELEMENTS_SELECTOR
    );
    (primaryActionRef.current ?? firstFocusable)?.focus();
    return () => {
      previouslyFocusedRef.current?.focus();
    };
  }, []);

  // Trap Tab / Shift-Tab inside the dialog so focus cannot escape to the page below.
  const handleModalKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    const focusable = Array.from(
      containerRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_ELEMENTS_SELECTOR) ?? []
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const modalTitleId = 'hp-confirmation-modal-title';

  return (
    <ModalOverlay data-testid="hp-confirmation-modal" onClick={handleOverlayClick}>
      <ModalContainer
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={modalTitleId}
        onKeyDown={handleModalKeyDown}
      >
        <ContentSection>
          <HeaderBodySection>
            <ModalHeader>
              <ModalTitle id={modalTitleId}>{title}</ModalTitle>
              <CloseButton type="button" onClick={onClose} aria-label="Close modal">
                <CloseOutlined />
              </CloseButton>
            </ModalHeader>
            <BodyText>{bodyText}</BodyText>
            {warningText && <WarningText>{warningText}</WarningText>}
          </HeaderBodySection>
          <ModalFooter>
            {showDontShowAgain && (
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={dontShowAgain}
                  onChange={(e) => onDontShowAgainChange?.(e.target.checked)}
                />
                Don&apos;t show again
              </CheckboxLabel>
            )}
            {!showDontShowAgain && <div style={{ flex: 1 }} />}
            <ButtonGroup>
              <OutlineButton
                type="button"
                data-testid="hp-confirmation-secondary"
                onClick={onSecondaryAction}
              >
                {SECONDARY_BUTTON_LABEL}
              </OutlineButton>
              <ActionButton
                ref={primaryActionRef}
                type="button"
                data-testid={primaryActionTestId}
                onClick={onPrimaryAction}
              >
                {PRIMARY_BUTTON_LABEL}
              </ActionButton>
            </ButtonGroup>
          </ModalFooter>
        </ContentSection>
      </ModalContainer>
    </ModalOverlay>
  );
};
